import type { CssSelectorMap } from '@tinct/core-api-models';

type ScreenSize = 'w400' | 'w600' | 'w800' | 'w1000' | 'w1200' | 'w1400';

// Ordered largest to smallest
// Keys match the camelCase-converted JSON keys from the API (w_1000 → w1000)
const SCREEN_SIZES: { key: ScreenSize; minWidth: number }[] = [
  { key: 'w1400', minWidth: 1400 },
  { key: 'w1200', minWidth: 1200 },
  { key: 'w1000', minWidth: 1000 },
  { key: 'w800', minWidth: 800 },
  { key: 'w600', minWidth: 600 },
  { key: 'w400', minWidth: 0 },
];

export interface SelectorCandidate {
  screenSize: string;
  selectors: string[];
}

/**
 * Returns all available screen sizes in resolution order for the given width.
 * First: the closest size >= width (searching upward if needed).
 * Then: descending through smaller sizes.
 */
export function getCandidatesForWidth(
  cssSelectors: CssSelectorMap,
  width: number,
): SelectorCandidate[] {
  let ceilingIndex = -1;
  for (let i = SCREEN_SIZES.length - 1; i >= 0; i--) {
    if (SCREEN_SIZES[i].minWidth > width) {
      ceilingIndex = i;
      break;
    }
  }

  const candidates: SelectorCandidate[] = [];

  // First: search upward from ceiling to find the closest size >= width
  if (ceilingIndex !== -1) {
    for (let i = ceilingIndex; i >= 0; i--) {
      const key = SCREEN_SIZES[i].key;
      const strategies = cssSelectors[key];
      if (strategies) {
        const selectors = Object.values(strategies).filter(Boolean);
        if (selectors.length > 0) {
          candidates.push({ screenSize: key, selectors });
          break;
        }
      }
    }
  }

  // Then: descend through sizes below the width
  const belowStart = ceilingIndex === -1 ? 0 : ceilingIndex + 1;
  for (let i = belowStart; i < SCREEN_SIZES.length; i++) {
    const key = SCREEN_SIZES[i].key;
    const strategies = cssSelectors[key];
    if (strategies) {
      const selectors = Object.values(strategies).filter(Boolean);
      if (selectors.length > 0) {
        candidates.push({ screenSize: key, selectors });
      }
    }
  }

  return candidates;
}
